import { ImageOff } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@shared/components/ui/cn';

import { useVaultFileStatus } from '../../hooks/use-vault-file-link';

import type { SentImage } from './file-meta';

interface ThumbnailStripProps {
  images: SentImage[];
  current: number;
  onSelect: (index: number, viaPointer: boolean) => void;
}

interface Overflow {
  start: boolean;
  end: boolean;
}

export function ThumbnailStrip({ images, current, onSelect }: ThumbnailStripProps) {
  const { t } = useTranslation('ai-cio');
  const viewportRef = useRef<HTMLDivElement>(null);
  const currentRef = useRef<HTMLButtonElement>(null);
  const hasScrolledRef = useRef(false);
  const [overflow, setOverflow] = useState<Overflow>({ start: false, end: false });

  const measureOverflow = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const offset = Math.abs(viewport.scrollLeft);
    const hiddenWidth = viewport.scrollWidth - viewport.clientWidth;
    const next = { start: offset > 1, end: hiddenWidth - offset > 1 };
    setOverflow((previous) =>
      previous.start === next.start && previous.end === next.end ? previous : next,
    );
  }, []);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    measureOverflow();
    const observer = new ResizeObserver(measureOverflow);
    observer.observe(viewport);
    return () => {
      observer.disconnect();
    };
  }, [measureOverflow, images.length]);

  useEffect(() => {
    currentRef.current?.scrollIntoView({
      block: 'nearest',
      inline: 'center',
      behavior: hasScrolledRef.current ? 'smooth' : 'instant',
    });
    hasScrolledRef.current = true;
  }, [current]);

  return (
    <div className="relative min-w-0 flex-1">
      <div
        ref={viewportRef}
        role="group"
        aria-label={t('imageViewer.thumbnails', 'Images in this message')}
        onScroll={measureOverflow}
        className="overflow-x-auto p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <div className="mx-auto flex w-fit gap-2">
          {images.map((image, index) => {
            const isCurrent = index === current;
            return (
              <Thumbnail
                key={image.id}
                image={image}
                index={index}
                isCurrent={isCurrent}
                buttonRef={isCurrent ? currentRef : undefined}
                onSelect={onSelect}
              />
            );
          })}
        </div>
      </div>
      {overflow.start && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 start-0 w-10 bg-linear-to-r from-background to-transparent rtl:bg-linear-to-l"
        />
      )}
      {overflow.end && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 end-0 w-10 bg-linear-to-l from-background to-transparent rtl:bg-linear-to-r"
        />
      )}
    </div>
  );
}

interface ThumbnailProps {
  image: SentImage;
  index: number;
  isCurrent: boolean;
  buttonRef?: RefObject<HTMLButtonElement | null>;
  onSelect: (index: number, viaPointer: boolean) => void;
}

function Thumbnail({ image, index, isCurrent, buttonRef, onSelect }: ThumbnailProps) {
  const { t } = useTranslation('ai-cio');
  const removed = useVaultFileStatus(image.vaultFileId) === 'removed';
  const descriptionId = useId();

  return (
    <button
      ref={buttonRef}
      type="button"
      aria-label={t('imageViewer.thumbnail', 'Open image {{n}}', { n: index + 1 })}
      aria-current={isCurrent ? 'true' : undefined}
      aria-describedby={removed ? descriptionId : undefined}
      onClick={(event) => {
        onSelect(index, event.detail > 0);
      }}
      className={cn(
        'h-16 w-13 shrink-0 cursor-pointer rounded-md border-2 p-0.5 focus-visible:outline-3 focus-visible:outline-offset-1 focus-visible:outline-neutral-500',
        isCurrent ? 'border-foreground' : 'border-transparent hover:border-[#a3a3a3]',
      )}
    >
      {removed ? (
        <>
          <span className="grid size-full place-items-center rounded-[3px] border bg-muted">
            <ImageOff className="size-4 text-muted-foreground" aria-hidden="true" />
          </span>
          <span id={descriptionId} className="sr-only">
            {t('fileCard.removedFromVault', 'Removed from Vault', { ns: 'common' })}
          </span>
        </>
      ) : (
        image.url && (
          <img
            src={image.url}
            alt=""
            draggable={false}
            className="size-full rounded-[3px] border object-cover"
          />
        )
      )}
    </button>
  );
}
